import * as React from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { CategoryIcon } from "./CashierCatalogGrid";
import type { CashierCatalogCategory, CashierLabels } from "./types";

export interface CashierCategoryTabsProps {
  categories: CashierCatalogCategory[];
  activeCategoryKey: string | null;
  onCategoryChange: (key: string | null) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  labels: CashierLabels;
}

export function CashierCategoryTabs({
  categories, activeCategoryKey, onCategoryChange, searchQuery, onSearchChange, labels,
}: CashierCategoryTabsProps) {
  const totalCount = categories.reduce((acc, c) => acc + c.count, 0);
  return (
    <div className="border-b border-biz-border bg-biz-surface-elevated">
      <div className="px-4 pt-3 pb-2">
        <label className="relative block">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-biz-foreground-subtle" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={labels.catalog.searchPlaceholder}
            className={cn(
              "h-11 w-full rounded-biz-btn border border-biz-border bg-biz-surface pl-10 pr-3 text-sm",
              "text-biz-foreground placeholder:text-biz-foreground-subtle biz-focus-ring",
            )}
          />
        </label>
      </div>
      <div className="px-4 pb-3 flex gap-2 overflow-x-auto biz-scrollbar">
        <CategoryChip
          isActive={activeCategoryKey === null}
          onClick={() => onCategoryChange(null)}
          label={labels.catalog.allCategory}
          count={totalCount}
        />
        {categories.map((c) => (
          <CategoryChip
            key={c.key}
            isActive={activeCategoryKey === c.key}
            onClick={() => onCategoryChange(c.key)}
            label={labels.categories[c.icon] ?? c.label}
            count={c.count}
            icon={
              <CategoryIcon
                icon={c.icon}
                className={cn("h-4 w-4", activeCategoryKey === c.key ? "" : "text-biz-foreground-muted")}
              />
            }
          />
        ))}
      </div>
    </div>
  );
}

function CategoryChip({
  isActive, onClick, label, count, icon,
}: { isActive: boolean; onClick: () => void; label: string; count: number; icon?: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        "h-11 px-3.5 rounded-biz-btn flex items-center gap-2 text-sm font-semibold whitespace-nowrap",
        "transition-colors duration-150 biz-focus-ring",
        isActive
          ? "bg-biz-primary text-biz-primary-foreground biz-shadow-sm"
          : "bg-biz-surface text-biz-foreground-muted border border-biz-border hover:bg-biz-muted",
      )}
    >
      {icon}
      <span>{label}</span>
      <span
        className={cn(
          "font-mono-num text-xs px-1.5 py-0.5 rounded-full tabular-nums",
          isActive ? "bg-white/20 text-biz-primary-foreground" : "bg-biz-muted text-biz-foreground-subtle",
        )}
      >
        {count}
      </span>
    </button>
  );
}
